import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, BarChart3, CheckCircle2, XCircle, TrendingUp } from "lucide-react";

interface AnalyticsData {
  totalExecutions: number;
  matched: number;
  noMatch: number;
  failed: number;
  byAutomation: { name: string; count: number }[];
}

interface AutomationAnalyticsProps {
  workspaceId: string;
}

export function AutomationAnalytics({ workspaceId }: AutomationAnalyticsProps) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) return;
    loadAnalytics();
  }, [workspaceId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const { data: logs } = await supabase
        .from("automation_execution_log")
        .select("id, final_status, automation_id")
        .eq("workspace_id", workspaceId)
        .gte("created_at", thirtyDaysAgo);

      const execLogs = (logs || []) as unknown as { id: string; final_status: string; automation_id: string }[];

      const matched = execLogs.filter((l) => l.final_status === "matched").length;
      const noMatch = execLogs.filter((l) => l.final_status === "no_match").length;
      const failed = execLogs.filter((l) => l.final_status === "failed").length;

      // Group by automation
      const autoCountMap = new Map<string, number>();
      for (const l of execLogs) {
        autoCountMap.set(l.automation_id, (autoCountMap.get(l.automation_id) || 0) + 1);
      }

      // Get automation names
      const autoIds = Array.from(autoCountMap.keys());
      let byAutomation: { name: string; count: number }[] = [];
      if (autoIds.length > 0) {
        const { data: autos } = await supabase
          .from("automations")
          .select("id, name")
          .in("id", autoIds);

        byAutomation = (autos || []).map((a: any) => ({
          name: a.name,
          count: autoCountMap.get(a.id) || 0,
        })).sort((a, b) => b.count - a.count).slice(0, 5);
      }

      setData({
        totalExecutions: execLogs.length,
        matched,
        noMatch,
        failed,
        byAutomation,
      });
    } catch (err) {
      console.error("Analytics error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <BarChart3 className="h-5 w-5" /> Últimos 30 dias
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              {data.totalExecutions}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Match</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              {data.matched}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Sem match</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">{data.noMatch}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Falhas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              {data.failed}
            </div>
          </CardContent>
        </Card>
      </div>

      {data.byAutomation.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Top automações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.byAutomation.map((a) => (
                <div key={a.name} className="flex items-center justify-between">
                  <span className="text-sm truncate max-w-[200px]">{a.name}</span>
                  <span className="text-sm font-medium text-muted-foreground">{a.count} execuções</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
